import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { API_ENDPOINTS } from "../../API/EndPoints";
import AxiosInstance from "../../API/AxiosInstance";
import { getMessaging, getToken } from "firebase/messaging";
import { firebaseApp } from "../../../call/firebase";

// ------------------ ASYNC THUNKS ------------------

// Register FCM token
export const registerFcmToken = createAsyncThunk(
  "call/registerFcmToken",
  async ({ userId, deviceType = "WEB" }, { rejectWithValue }) => {
    try {
      const messaging = getMessaging(firebaseApp);

      // Request permission for notifications
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        throw new Error("Notification permission not granted");
      }

      // Get FCM token
      const token = await getToken(messaging, {
        vapidKey:
          "BIP0jG_zra_h6Xub0B1fz2ZKqhBMIEQ-xmf2vUIqUJnrXAy8AnogksbVvq7HY-MdXIQJmLOVi5awl7imkIK2Fjg",
      });

      if (!token) throw new Error("No FCM token generated");

      // Call backend API to register FCM token
      const response = await AxiosInstance.post(API_ENDPOINTS.REGISTER_FCM, {
        fcmToken: token,
        userId,
        deviceType,
      });

      return { token, response: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "Something went wrong"
      );
    }
  }
);
// Start Call
export const startCall = createAsyncThunk(
  "call/startCall",
  async ({ patientId, appointmentId }, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(API_ENDPOINTS.START_CALL, {
        patientId,
        appointmentId,
      });
      return response.data; // should include roomCode
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to start call"
      );
    }
  }
);

// Join Call
export const joinCall = createAsyncThunk(
  "call/joinCall",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        API_ENDPOINTS.JOIN_CALL,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to join call"
      );
    }
  }
);

// End Call
export const endCall = createAsyncThunk(
  "call/endCall",
  async ({ roomCode }, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(API_ENDPOINTS.END_CALL, {
        roomCode,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to end call"
      );
    }
  }
);

// Send Offer
export const sendOffer = createAsyncThunk(
  "call/sendOffer",
  async ({ roomCode, sdp, senderId }, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        `${API_ENDPOINTS.SIGNALING}/${roomCode}/offer`,
        { sdp, type: "offer", senderId }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send offer"
      );
    }
  }
);

// Send Answer
export const sendAnswer = createAsyncThunk(
  "call/sendAnswer",
  async ({ roomCode, sdp, senderId }, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        `${API_ENDPOINTS.SIGNALING}/${roomCode}/answer`,
        { sdp, type: "answer", senderId }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send answer"
      );
    }
  }
);

// Send ICE Candidate
export const sendIceCandidate = createAsyncThunk(
  "call/sendIceCandidate",
  async (
    { roomCode, candidate, sdpMid, sdpMLineIndex, senderId },
    { rejectWithValue }
  ) => {
    try {
      const response = await AxiosInstance.post(
        `${API_ENDPOINTS.SIGNALING}/${roomCode}/ice`,
        { candidate, sdpMid, sdpMLineIndex, senderId }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send ICE candidate"
      );
    }
  }
);

// Fetch Signaling Messages (polling)
export const fetchMessages = createAsyncThunk(
  "call/fetchMessages",
  async (roomCode, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get(
        `${API_ENDPOINTS.SIGNALING}/${roomCode}/messages`
      );
      return response.data; // should include offers/answers/ice
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch messages"
      );
    }
  }
);

// Get Call Notification
export const fetchNotification = createAsyncThunk(
  "call/fetchNotification",
  async ({ doctorId, patientId, roomId }, { rejectWithValue }) => {
    try {
      // build query dynamically
      const params = new URLSearchParams();
      if (doctorId) params.append("doctorId", doctorId);
      if (patientId) params.append("patientId", patientId);
      if (roomId) params.append("roomId", roomId);

      const response = await AxiosInstance.get(
        `${API_ENDPOINTS.NOTIFICATION}?${params.toString()}`
      );

      return response.data; // should include roomCode, signalingUrl, iceServers
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch notification"
      );
    }
  }
);

// ------------------ INITIAL STATE ------------------
const initialState = {
  fcm: {
    token: null,
    loading: false,
    error: null,
  },
  call: {
    loading: false,
    error: null,
    data: null,
  },
  signaling: {
    messages: [],
    error: null,
  },
  notification: {
    loading: false,
    data: null,
    error: null,
  },
  incomingCall: null,
};

// ------------------ SLICE ------------------
const CallSlice = createSlice({
  name: "Call",
  initialState,
  reducers: {
    setIncomingCall: (state, action) => {
      state.incomingCall = action.payload;
    },
    clearIncomingCall: (state) => {
      state.incomingCall = null;
    },
    clearMessages: (state) => {
      state.signaling.messages = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Register FCM Token
      .addCase(registerFcmToken.pending, (state) => {
        state.fcm.loading = true;
        state.fcm.error = null;
      })
      .addCase(registerFcmToken.fulfilled, (state, action) => {
        state.fcm.loading = false;
        state.fcm.token = action.payload.token;
      })
      .addCase(registerFcmToken.rejected, (state, action) => {
        state.fcm.loading = false;
        state.fcm.error = action.payload;
      })

      // Start Call
      .addCase(startCall.pending, (state) => {
        state.call.loading = true;
        state.call.error = null;
      })
      .addCase(startCall.fulfilled, (state, action) => {
        state.call.loading = false;
        state.call.data = action.payload;
      })
      .addCase(startCall.rejected, (state, action) => {
        state.call.loading = false;
        state.call.error = action.payload;
      })

      // Join Call
      .addCase(joinCall.fulfilled, (state, action) => {
        state.call.data = action.payload;
      })

      // End Call
      .addCase(endCall.fulfilled, (state) => {
        state.call.data = null;
      })

      // Messages
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.signaling.messages = action.payload;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.signaling.error = action.payload;
      })
      .addCase(fetchNotification.pending, (state) => {
        state.notification.loading = true;
        state.notification.error = null;
      })
      .addCase(fetchNotification.fulfilled, (state, action) => {
        state.notification.loading = false;
        state.notification.data = action.payload.data; // roomCode, signalingUrl, iceServers
      })
      .addCase(fetchNotification.rejected, (state, action) => {
        state.notification.loading = false;
        state.notification.error = action.payload;
      });
  },
});

export const { setIncomingCall, clearIncomingCall, clearMessages } =
  CallSlice.actions;
export default CallSlice.reducer;
